import { BenchmarkingItem, CommonProps } from '@/stores/benchmarking-store';
import { ScenarioInputs, ScenarioType } from '@/types/scenario';
import { TransferPair } from '@/types/transfer-pair';
import { calculateScenario } from './scenario-calculations';

export interface ScenarioSectionItem {
  label: string;
  value: string;
  valueStyle?: any;
}

export interface InvoicingSection {
  title: string;
  icon?: string;
  exchangeRate: {
    label: any;
    value: string;
  };
  localInvoicingAmount: {
    label: any;
    value: string;
  };
  vatRate: {
    label: any;
    value: string;
  };
  estimatedLocalInvoice: {
    label: any;
    value: string;
  };
}

export interface CostSection {
  title: string;
  icon?: string;
  netRevenueExclusions: {
    label: any;
    value: string;
  };
  sharedMargin?: {
    label: any;
    value: string;
  };
  financialStructure?: {
    label: any;
    value: string;
  };
  indirectCosts?: {
    label: any;
    value: string;
  };
}
export interface ResultSection {
  title: string;
  icon?: string;
  estimatedPayment: {
    label: any;
    value: string;
  };
  estimatedPaymentUSD: {
    label: any;
    value: string;
  };
}

export interface ScenarioPresentation {
  title: string;
  sections: {
    invoicing: InvoicingSection;
    costs: CostSection;
    results: ResultSection;
  };
  highlight: { label: string; value: string };
  raw: { paymentAmountToReceiveUSD: number };
}

// Loosely typed to avoid heavy i18n dependencies
type TranslateFunction = any;

interface Formatters {
  formatCurrency: (amount: number, currencyISO: string, decimals?: number) => string;
  formatNumber: (value: number, decimals?: number) => string;
  formatPercentage: (value: number) => string;
}

export const getScenarioPresentation = (
  type: ScenarioType,
  data: TransferPair,
  inputs: ScenarioInputs,
  t: TranslateFunction,
  formatters: Formatters,
): ScenarioPresentation => {
  const {
    exchangeRate,
    localInvoicingAmount,
    vatRate,
    vatAmount,
    nonRecTaxes,
    nonRecTaxesAmount,
    financialStructure,
    partnerMargin,
    partnerMarginAmount,
    whtAmount,
    transactionCost,
    paymentAmountToReceive,
    paymentAmountToReceiveUSD,
  } = calculateScenario(type, data, inputs);

  const { formatCurrency, formatNumber, formatPercentage } = formatters;
  const customerCurrency = type === 'internationalInvoicing' ? 'USD' : data.originCurrencyISO;

  const invoicing = {
    exchangeRate: {
      label: t('scenarios.card.exchangeRate', 'Exchange Rate'),
      value: formatCurrency(exchangeRate, customerCurrency, 2),
    },
    localInvoicingAmount: {
      label: t('scenarios.card.invoiceAmountLocal', 'Invoice Amount in Local Currency'),
      value: formatCurrency(localInvoicingAmount, customerCurrency),
    },
    vatRate: {
      label: t('scenarios.card.localVat', 'Local VAT ({{rate}})', { rate: formatPercentage(vatRate) }),
      value: formatCurrency(vatAmount, customerCurrency),
    },
    estimatedLocalInvoice: {
      label: t('scenarios.card.estimatedLocalInvoice', 'Estimated Local Invoice Amount to Local Customer'),
      value: formatCurrency(localInvoicingAmount * (1 + vatRate), customerCurrency),
    },
  };

  const costs = {
    netRevenueExclusions: {
      label: t('scenarios.card.netRevenueExclusions', 'Net revenue exclusions'),
      value: formatCurrency(whtAmount + transactionCost + nonRecTaxesAmount, customerCurrency),
    },
    sharedMargin: type === 'localPartner' ? {
      label: t('scenarios.card.sharedMargin', 'Shared Marging With Local Partner ({{rate}})', {
        rate: formatPercentage(partnerMargin),
      }),
      value: formatCurrency(partnerMarginAmount, customerCurrency),
    } : undefined,
    financialStructure: type === 'localSubsidiary' ? {
      label: t('scenarios.card.financialStructure'),
      value: formatCurrency(financialStructure, customerCurrency),
    } : undefined,
    indirectCosts: type === 'internationalInvoicing' ? {
      label: t('scenarios.card.indirectCosts.label'),
      value: t('scenarios.card.indirectCosts.value'),
    } : undefined,
  };

  const results = {
    estimatedPayment: {
      label: t('scenarios.card.estimatedPayment', 'Estimated Payment Amount to Receive'),
      value: formatCurrency(paymentAmountToReceive, customerCurrency),
    },
    estimatedPaymentUSD: {
      label: t('scenarios.card.estimatedPaymentUSD', 'Estimated Payment Amount to Receive USD'),
      value: formatCurrency(paymentAmountToReceiveUSD, 'USD'),
    },
  };

  return {
    title: t(`scenarios.types.${type}.title`),
    sections: {
      invoicing: { title: t('scenarios.card.invoicing'), icon: 'doc.text', ...invoicing },
      costs: { title: t('scenarios.card.costs'), icon: 'chart.bar', ...costs },
      results: { title: t('scenarios.card.results'), icon: 'chart.bar', ...results },
    },
    highlight: {
      label: t('scenarios.card.estimatedPaymentUSD'),
      value: formatCurrency(paymentAmountToReceiveUSD, 'USD'),
    },
    // Raw values might still be useful for Resume view or other logic
    raw: {
      paymentAmountToReceiveUSD,
    },
  };
};

export const getBenchmarkingPresentation = (
  commonProps: CommonProps,
  item: Omit<BenchmarkingItem, 'id'>,
  data: TransferPair,
  t: TranslateFunction,
  formatters: Formatters,
): ScenarioPresentation | null => {
  if (!commonProps.scenario) return null;

  const inputs: ScenarioInputs = {
    invoicingAmount: commonProps.invoicingAmount,
    partnerMargin: commonProps.partnerMargin,
    vendorIdList: commonProps.vendorIdList,
    vendorId: item.vendorId,
    invoicingCurrency: 'USD',
    localCurrency: item.localCurrency,
    sellThroughLocalPartner: commonProps.scenario === 'localPartner',
    customerId: item.customerId,
    invoicingConcept: item.invoicingConcept,
    selectedScenarios: [],
  };

  return getScenarioPresentation(commonProps.scenario, data, inputs, t, formatters);
};
